import React, { useRef, createContext, MutableRefObject } from 'react';
import {
  ValidationGroup,
  IValidationGroupOptions
} from 'devextreme-react/validation-group';

type ValidationGroupChildren = (() => JSX.Element | null) | JSX.Element | null;

type ValidationGroupFnProps = Omit<IValidationGroupOptions, 'children'> & {
  children: ValidationGroupChildren;
};

export type RenderValidationGroup = <TFormIds extends string>(
  {
    formId,
    validationGroupProps
  }: {
    formId: FormId<TFormIds>;
    validationGroupProps: Omit<ValidationGroupFnProps, 'children'>;
  },
  children: ValidationGroupChildren
) => JSX.Element | null;

type ValidationGroupFn = (props: ValidationGroupFnProps) => JSX.Element;

type FormValidationResult = { isValid: boolean };

interface ValidationGroupInstance {
  validate(): FormValidationResult;
}

type ValidationGroupsInstances<TFormIds extends string> = Record<
  TFormIds,
  ValidationGroupInstance
>;

type ValidationGroupsFns<TFormIds extends string> = Record<
  TFormIds,
  ValidationGroupFn
>;

export type ValidateForm = <TFormIds extends string>(
  formId: FormId<TFormIds>
) => FormValidationResult;

type FormId<
  TFormIds extends string
> = keyof ValidationGroupsInstances<TFormIds>;

type FormValidationContextValue = {
  renderValidationGroup: RenderValidationGroup;
  validateForm: ValidateForm;
};

export const FormValidationContext = createContext<FormValidationContextValue>(
  {} as FormValidationContextValue
);

function createValidationGroupRenderer<TFormIds extends string>(
  validationGroupsInstances: MutableRefObject<
    ValidationGroupsInstances<TFormIds>
  >,
  formId: FormId<TFormIds>
) {
  return (props: ValidationGroupFnProps) => {
    const { children } = props;

    return (
      <ValidationGroup
        {...props}
        onInitialized={(e) => {
          const instance = e.component?.instance();

          if (instance) {
            // eslint-disable-next-line no-param-reassign
            validationGroupsInstances.current[
              formId
            ] = instance as ValidationGroupInstance;
          }
        }}
      >
        {typeof children === 'function' ? children() : children}
      </ValidationGroup>
    );
  };
}

function createValidationGroupsFns<TFormIds extends string>(
  formIds: FormId<TFormIds>[],
  validationGroupsInstances: MutableRefObject<
    ValidationGroupsInstances<TFormIds>
  >
) {
  return formIds.reduce<ValidationGroupsFns<TFormIds>>((acc, formId) => {
    return {
      ...acc,
      [formId]: createValidationGroupRenderer(validationGroupsInstances, formId)
    };
  }, {} as ValidationGroupsFns<TFormIds>);
}

export function withFormValidation<TFormIds extends string>(
  WrappedComponent: (props: any) => JSX.Element,
  formIds: TFormIds[]
) {
  return (props: any) => {
    const validationGroupsInstances = useRef<
      ValidationGroupsInstances<TFormIds>
    >({} as ValidationGroupsInstances<TFormIds>);
    const validationGroupsFns = useRef(
      createValidationGroupsFns(formIds, validationGroupsInstances)
    );

    const renderValidationGroup: RenderValidationGroup = (
      { formId, validationGroupProps },
      children
    ) => {
      const valGroupFn =
        validationGroupsFns.current[(formId as unknown) as FormId<TFormIds>];

      if (!valGroupFn) return null;

      return valGroupFn({
        ...validationGroupProps,
        children
      });
    };

    const validateForm: ValidateForm = (formId) => {
      const valGroupInstance =
        validationGroupsInstances.current[
          (formId as unknown) as FormId<TFormIds>
        ];

      if (!valGroupInstance) {
        return { isValid: false };
      }

      return valGroupInstance.validate();
    };

    return (
      <FormValidationContext.Provider
        value={{ renderValidationGroup, validateForm }}
      >
        <WrappedComponent
          {...props}
          renderValidationGroup={renderValidationGroup}
          validateForm={validateForm}
        />
      </FormValidationContext.Provider>
    );
  };
}

export default withFormValidation;
